import { useCallback } from 'react'
import {
  fetchCustomerMe,
  logoutCustomer,
  refreshCustomerSession,
  updateCustomerProfile,
} from '../../customerAuthApi'
import type { CustomerProfileUpdateInput } from '../../types'
import { asAuthErrorMessage } from '../../helpers/errors'
import { runBusyAuthAction } from './runAuthAction'
import type {
  ApplyAuthenticatedStateFn,
  ClearAuthStateFn,
  CustomerAuthActionState,
} from './actionTypes'

type Params = CustomerAuthActionState & {
  token: string | null
  applyAuthenticatedState: ApplyAuthenticatedStateFn
  clearAuthState: ClearAuthStateFn
}

export const useCustomerSessionActions = ({
  token,
  applyAuthenticatedState,
  clearAuthState,
  setBusy,
  setError,
}: Params) => {
  const refresh = useCallback(async () => {
    try {
      const session = await refreshCustomerSession()
      try {
        const me = await fetchCustomerMe()
        applyAuthenticatedState(session.token, me.account, me.profile)
      } catch {
        applyAuthenticatedState(session.token, session.account, session.profile)
      }
      return true
    } catch (err) {
      setError(asAuthErrorMessage(err))
      clearAuthState()
      return false
    }
  }, [applyAuthenticatedState, clearAuthState, setError])

  const patchProfile = useCallback(
    async (input: CustomerProfileUpdateInput) => {
      let activeToken = token
      if (!activeToken) {
        try {
          const session = await refreshCustomerSession()
          activeToken = session.token
          applyAuthenticatedState(session.token, session.account, session.profile)
        } catch {
          clearAuthState({ clearStorage: false, broadcast: false })
          return false
        }
      }

      return runBusyAuthAction({
        setBusy,
        setError,
        action: async () => {
          const payload = await updateCustomerProfile(input)
          applyAuthenticatedState(activeToken, payload.account, payload.profile)
          return true
        },
        onErrorResult: false,
      })
    },
    [applyAuthenticatedState, clearAuthState, setBusy, setError, token],
  )

  const logout = useCallback(async () => {
    let activeToken = token
    if (!activeToken) {
      try {
        const session = await refreshCustomerSession()
        activeToken = session.token
      } catch {
        clearAuthState()
        return
      }
    }
    await runBusyAuthAction({
      setBusy,
      setError,
      action: async () => {
        try {
          await logoutCustomer()
        } catch {
          // local logout still has priority if remote revoke fails
        }
        clearAuthState()
        return true
      },
      onErrorResult: false,
    })
  }, [clearAuthState, setBusy, setError, token])

  return {
    refresh,
    logout,
    patchProfile,
  }
}
